import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw, WifiOff, ServerCrash, Clock, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

export type ErrorType = 'network' | 'server' | 'timeout' | 'not-found' | 'generic';

interface ErrorFallbackProps {
  title?: string;
  message?: string;
  type?: ErrorType;
  onRetry?: () => void;
  isRetrying?: boolean;
  showHomeLink?: boolean;
  className?: string;
  compact?: boolean;
  /** Countdown seconds until auto-retry (null = no countdown) */
  countdownSeconds?: number | null;
  /** Number of retry attempts made */
  retryCount?: number;
  /** Max retries (to show exhausted state) */
  maxRetries?: number;
}

const ERROR_CONFIGS: Record<ErrorType, { icon: typeof AlertCircle; defaultTitle: string; defaultMessage: string }> = {
  network: {
    icon: WifiOff,
    defaultTitle: 'Connection Problem',
    defaultMessage: 'Unable to connect. Please check your internet connection and try again.',
  },
  server: {
    icon: ServerCrash,
    defaultTitle: 'Server Issue',
    defaultMessage: "Our servers are experiencing issues. Please try again in a few moments.",
  },
  timeout: {
    icon: Clock,
    defaultTitle: 'Request Timeout',
    defaultMessage: 'The request took too long. Please try again.',
  },
  'not-found': {
    icon: AlertCircle,
    defaultTitle: 'Not Found',
    defaultMessage: "We couldn't find what you're looking for.",
  },
  generic: {
    icon: AlertCircle,
    defaultTitle: 'Something Went Wrong',
    defaultMessage: "We're having trouble loading data right now. Please try again in a few moments.",
  },
};

/**
 * Reusable error fallback component for displaying user-friendly error states
 * Now supports auto-retry countdown display
 */
export function ErrorFallback({
  title,
  message,
  type = 'generic',
  onRetry,
  isRetrying = false,
  showHomeLink = false,
  className,
  compact = false,
  countdownSeconds = null,
  retryCount = 0,
  maxRetries = 3,
}: ErrorFallbackProps) {
  const config = ERROR_CONFIGS[type];
  const Icon = config.icon;
  const displayTitle = title || config.defaultTitle;
  const displayMessage = message || config.defaultMessage;
  const isExhausted = retryCount >= maxRetries;

  // Determine retry button text
  const getRetryButtonText = () => {
    if (isRetrying) return 'Retrying...';
    if (countdownSeconds !== null && countdownSeconds > 0) {
      return `Retry in ${countdownSeconds}s`;
    }
    if (isExhausted) return 'Try Again';
    return 'Try Again';
  };

  if (compact) {
    return (
      <div className={cn(
        'flex items-center gap-3 p-4 rounded-lg bg-muted/50 border border-border',
        className
      )}>
        <div className="p-2 rounded-full bg-destructive/10">
          <Icon className="h-4 w-4 text-destructive" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">{displayTitle}</p>
          <p className="text-xs text-muted-foreground truncate">{displayMessage}</p>
          {countdownSeconds !== null && countdownSeconds > 0 && !isRetrying && (
            <p className="text-xs text-primary mt-0.5 tabular-nums">
              Auto-retry in {countdownSeconds}s...
            </p>
          )}
        </div>
        {onRetry && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRetry}
            disabled={isRetrying}
            className="shrink-0"
          >
            <RefreshCw className={cn('h-4 w-4', isRetrying && 'animate-spin')} />
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card className={cn('border-dashed', className)}>
      <CardHeader className="text-center pb-2">
        <div className="mx-auto mb-4 p-4 rounded-full bg-destructive/10">
          <Icon className="h-8 w-8 text-destructive" />
        </div>
        <CardTitle className="text-lg">{displayTitle}</CardTitle>
        <CardDescription className="max-w-sm mx-auto">
          {displayMessage}
        </CardDescription>
        
        {/* Auto-retry countdown indicator */}
        {countdownSeconds !== null && countdownSeconds > 0 && !isRetrying && !isExhausted && (
          <div className="mt-3 flex items-center justify-center gap-2">
            <div className="relative h-6 w-6">
              <svg className="h-6 w-6 -rotate-90" viewBox="0 0 24 24">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-muted"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray={62.83}
                  strokeDashoffset={62.83 * (1 - countdownSeconds / 15)}
                  className="text-primary transition-all duration-1000"
                />
              </svg>
            </div>
            <span className="text-sm text-muted-foreground tabular-nums">
              Auto-retry in {countdownSeconds}s
            </span>
          </div>
        )}
        
        {/* Retry attempt indicator */}
        {retryCount > 0 && !isExhausted && (
          <p className="mt-2 text-xs text-muted-foreground">
            Attempt {retryCount} of {maxRetries}
          </p>
        )}
        
        {/* Exhausted state */}
        {isExhausted && (
          <p className="mt-2 text-xs text-destructive">
            Auto-retry exhausted. Please try manually.
          </p>
        )}
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-3 pt-2">
        {onRetry && (
          <Button
            variant="outline"
            onClick={onRetry}
            disabled={isRetrying}
            className="gap-2 min-w-[140px]"
          >
            <RefreshCw className={cn('h-4 w-4', isRetrying && 'animate-spin')} />
            {getRetryButtonText()}
          </Button>
        )}
        {showHomeLink && (
          <Button variant="ghost" asChild size="sm">
            <Link to="/" className="gap-2">
              <Home className="h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Inline error message for partial failures
 */
interface InlineErrorProps {
  message: string;
  onRetry?: () => void;
  isRetrying?: boolean;
  className?: string;
}

export function InlineError({ message, onRetry, isRetrying, className }: InlineErrorProps) {
  return (
    <div className={cn(
      'flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/20',
      className
    )}>
      <div className="flex items-center gap-2 min-w-0">
        <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
        <span className="text-xs text-destructive truncate">{message}</span>
      </div>
      {onRetry && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRetry}
          disabled={isRetrying}
          className="h-6 px-2 text-xs text-destructive hover:text-destructive"
        >
          <RefreshCw className={cn('h-3 w-3', isRetrying && 'animate-spin')} />
        </Button>
      )}
    </div>
  );
}

/**
 * Data unavailable banner for partial load scenarios
 */
interface DataUnavailableBannerProps {
  dataType: string;
  className?: string;
}

export function DataUnavailableBanner({ dataType, className }: DataUnavailableBannerProps) {
  return (
    <div className={cn(
      'flex items-center gap-2 px-3 py-2 rounded-md bg-muted border border-border/50 text-muted-foreground',
      className
    )}>
      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
      <span className="text-xs">{dataType} temporarily unavailable</span>
    </div>
  );
}
